import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DELETE_TABLE_API,
  GET_TABLE_DETAILS_API,
  NO_TABLE_GUEST_OPTIONS_API,
  UPDATE_TABLE_API,
} from "../../Constant/api";
import { useToast } from "../../Components/Toast";
import TextboxSet from "../../Components/TextboxSet";
import Counter from "../../Components/Counter";
import Button from "../../Components/Button";
import LoadingInComponent from "../../Components/LoadingInComponent";
import WidgetCard from "../../Components/WidgetCard";
import axios from "axios";
import SectionCard from "../../Components/SectionCard";
import TimestampCard from "../../Components/TimestampCard";
import { SEATS_MANAGEMENT } from "../../Router/path";
import SeatManagementEditTable from "./SeatManagementEditTable";
import Modal from "../../Components/Modal";
import { Warning2 } from "iconsax-react";
import Breadcrumb from "../../Components/Breadcrumb";
import Can from "../../Components/Can";

const SeatManagementEdit = () => {
  const [loading, setLoading] = useState(false);
  const [tableInfo, setTableInfo] = useState();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    pax: 1,
  });
  const [guestList, setGuestList] = useState();
  const [guestListOption, setGuestListOption] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const tableId = location.state.tableId;

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchTableDetail();
    }
  };

  const fetchGuestList = useCallback(async () => {
    setLoading(true);
    axios
      .get(NO_TABLE_GUEST_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((response) => {
        setLoading(false);
        setGuestListOption(response.data.data);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to fetch guest list", "error");
      });
  }, [toast]);

  const fetchTableDetail = useCallback(async (isClear = false) => {
    setLoading(true);
    axios
      .get(GET_TABLE_DETAILS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          tableId: tableId,
          search: isClear ? "" : searchTerm,
        },
      })
      .then((response) => {
        setLoading(false);
        setTableInfo(response.data.data);
        setFormData({
          name: response.data.data.name,
          description: response.data.data.description,
          pax: response.data.data.pax,
        });
        setGuestList(response.data.data.guest_list);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to fetch table detail", "error");
      });
  }, [tableId, searchTerm, toast]);

  const handleRefresh = () => {
    fetchGuestList();
    fetchTableDetail();
  };

  const handleUpdate = async () => {
    setLoading(true);
    axios
      .put(UPDATE_TABLE_API, formData, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          tableId: tableId,
        },
      })
      .then((response) => {
        setLoading(false);
        navigate(SEATS_MANAGEMENT);
        toast("Table updated successfully", "success");
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast(error.response.data.message, "error");
      });
  };

  const handleDelete = async () => {
    setLoading(true);
    axios
      .delete(DELETE_TABLE_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          tableId: tableId,
        },
      })
      .then((response) => {
        setLoading(false);
        navigate(SEATS_MANAGEMENT);
        toast("Table deleted successfully", "success");
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to delete table", "error");
      });
  };

  const handleChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  useEffect(() => {
    fetchGuestList();
    fetchTableDetail();
  }, [fetchGuestList, fetchTableDetail]);

  const breadcrumbItems = [
    { label: "Seats Management", to: "/seats-management" },
    { label: tableInfo ? `Table ${tableInfo.name}` : "Table" },
    { label: "Edit" },
  ];

  return (
    <div className="flex flex-col w-full h-full">
      {showModal && (
        <Modal toggleModal={toggleModal} confirmation={handleDelete}>
          <div className="flex flex-col w-full items-center justify-center gap-[16px] py-[32px]">
            <Warning2 size="64" color="#ff0000" variant="Bold" />
            <p>Are you sure you want to delete this table?</p>
          </div>
        </Modal>
      )}
      {/* Create modal */}
      <LoadingInComponent isLoading={loading}>
        {tableInfo && (
          <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[12px] md:gap-[24px]">
            {/* Breadcrumb */}
            <Breadcrumb items={breadcrumbItems} />

            {/* Title row */}
            <div className="flex w-full justify-between items-center">
              <h1 className="text-[29px]">Table {tableInfo.name} Edit</h1>
              <Can permission="delete_seat">
                <Button variant="error" onClick={toggleModal}>
                  Delete
                </Button>
              </Can>
            </div>

            {/* Table Detail Form */}
            <div className="flex flex-col md:flex-row w-full gap-[12px] md:gap-[24px]">
              <SectionCard>
                <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-[24px] p-[24px]">
                  <TextboxSet
                    label="Table Name"
                    name="name"
                    type="text"
                    value={formData.name}
                    required
                    onChange={(e) => handleChange(e)}
                  />
                  <Counter
                    title="Number of seats"
                    required
                    formData={formData}
                    setFormData={setFormData}
                  />
                  <div className="md:col-span-2">
                    <TextboxSet
                      label="Table Description"
                      name="description"
                      type="textarea"
                      value={formData.description}
                      onChange={(e) => handleChange(e)}
                    />
                  </div>
                </div>
              </SectionCard>

              <SectionCard className="md:max-w-[300px]">
                <TimestampCard
                  created_at={tableInfo.created_at}
                  updated_at={tableInfo.updated_at}
                />
              </SectionCard>
            </div>

            {/* Button */}
            <div className="flex w-full gap-[8px]">
              <Button onClick={handleUpdate}>Save Changes</Button>
              <Button
                variant="secondary"
                onClick={() => navigate(SEATS_MANAGEMENT)}
              >
                Cancel
              </Button>
            </div>

            {/* Widgets */}
            <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-[12px] md:gap-[24px]">
              <WidgetCard
                name="Total Check-In Guests"
                data={tableInfo.widget.total_guests_checked_in}
              />
              <WidgetCard
                name="Assigned/Seats"
                data={tableInfo.widget.assigned_guests}
              />
              <WidgetCard
                name="Special Dietary"
                data={tableInfo.widget.special_dietary}
              />
            </div>

            {/* Table */}
            {guestList && (
              <SeatManagementEditTable
                tableId={tableId}
                data={guestList}
                guestList={guestListOption}
                refresh={handleRefresh}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                handleKeyDown={handleKeyDown}
                clearFilter={() => {
                  setSearchTerm("");
                  fetchTableDetail(true);
                }}
              />
            )}
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default SeatManagementEdit;
